// Iter44 — TMS Settings card (TMS Module Configuration).
//
// Surfaces the tenant-default vehicle-data countdown + the contracted
// lane rates table. Tenant-admin (or higher) only — the parent Settings
// page wraps this with <RoleGuard minRole="tenant_admin">.
//
// Two backend integrations:
//   - GET/PATCH /api/tms/settings (countdown default)
//   - GET/POST/PATCH/DELETE /api/tms/contracted-rates (lane rates)
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useTenants } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTDOWN_PRESETS = [15, 30, 45, 60, 90, 120, 180];

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "#f0f2f8",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export const TmsSettingsCard = () => {
  const addToast = useStore((s) => s.addToast);
  const [minutes, setMinutes] = useState("60");
  const [systemDefault, setSystemDefault] = useState(60);
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null); // null|"new"|<rate_id>
  const { data: tenants = [] } = useTenants();

  const loadAll = async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/tms/settings`),
        axios.get(`${API}/tms/contracted-rates`),
      ]);
      setMinutes(String(s.data.default_vehicle_data_deadline_minutes));
      setSystemDefault(s.data.system_default_minutes);
      setRates(r.data || []);
    } catch (e) {
      addToast({ type: "error", title: "Failed to load TMS settings", body: e?.response?.data?.detail || e.message });
    }
  };
  useEffect(() => { loadAll(); }, []);

  const saveCountdown = async () => {
    const n = parseInt(minutes, 10);
    if (!Number.isFinite(n) || n < 1 || n > 1440) {
      addToast({ type: "error", title: "Invalid value", body: "Minutes must be between 1 and 1440." });
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/tms/settings`, { default_vehicle_data_deadline_minutes: n });
      addToast({ type: "success", title: "TMS default countdown saved", body: `${n} min` });
      await loadAll();
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md p-5 mt-5"
      style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}
      data-testid="settings-tms-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <span
          className="rounded-md flex items-center justify-center"
          style={{
            width: 38, height: 38,
            background: "#185FA522", border: "1px solid #185FA566",
            color: "#9ec3ec",
            ...mono(14, undefined, 700),
          }}
        >T</span>
        <div className="flex-1">
          <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
            TMS Configuration
          </div>
          <div className="mt-0.5" style={mono(10, "#9198b0")}>
            Vehicle-data countdown · contracted lane rates · auto-award rules
          </div>
        </div>
      </div>

      {/* Countdown */}
      <div className="mt-3" data-testid="settings-tms-countdown-section">
        <div className="uppercase mb-1.5" style={mono(9, "#9198b0", 600, "0.8px")}>
          Default vehicle-data countdown (minutes)
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            type="number"
            min={1}
            max={1440}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            className="px-2 py-1.5 rounded outline-none"
            style={{
              background: "#08090d", border: "1px solid rgba(255,255,255,0.10)",
              color: "#f0f2f8", width: 90, ...mono(11),
            }}
            data-testid="settings-tms-countdown-input"
          />
          {COUNTDOWN_PRESETS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMinutes(String(m))}
              className="px-2 py-1 rounded"
              style={{
                background: String(m) === String(minutes) ? "rgba(24,95,165,0.18)" : "transparent",
                border: `1px solid ${String(m) === String(minutes) ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                color: String(m) === String(minutes) ? "#9ec3ec" : "#c5cad8",
                ...mono(10, undefined, 600, "0.4px"),
                cursor: "pointer",
              }}
              data-testid={`settings-tms-countdown-preset-${m}`}
            >{m}m</button>
          ))}
          <button
            type="button"
            onClick={saveCountdown}
            disabled={busy}
            className="px-3 py-1.5 rounded ml-2"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
            data-testid="settings-tms-countdown-save"
          >{busy ? "Saving…" : "Save"}</button>
        </div>
        <div className="mt-1.5" style={mono(9, "#9198b0")}>System default: {systemDefault}m.</div>
      </div>

      {/* Contracted Lane Rates */}
      <div className="mt-5" data-testid="settings-tms-rates-section">
        <div className="flex items-center justify-between mb-2">
          <div className="uppercase" style={mono(9, "#9198b0", 600, "0.8px")}>
            Contracted lane rates
          </div>
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="px-2.5 py-1 rounded"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid="settings-tms-rate-new-btn"
          >+ Add lane</button>
        </div>
        <div className="rounded overflow-hidden"
          style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)" }}>
          <div className="grid grid-cols-12 gap-2 px-3 py-2"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", ...mono(9, "#9198b0", 600, "0.8px") }}>
            <div className="col-span-3 uppercase">Carrier</div>
            <div className="col-span-3 uppercase">Origin city</div>
            <div className="col-span-3 uppercase">Destination city</div>
            <div className="col-span-2 uppercase text-right">Rate (EUR)</div>
            <div className="col-span-1"></div>
          </div>
          {rates.length === 0 ? (
            <div className="px-3 py-3" style={mono(11, "#9198b0")} data-testid="settings-tms-rates-empty">
              No contracted lane rates yet. Standard transport requests will fall back to bidding.
            </div>
          ) : rates.map((r) => (
            <RateRow
              key={r.id}
              row={r}
              tenants={tenants}
              isEditing={editing === r.id}
              onEdit={() => setEditing(r.id)}
              onCancel={() => setEditing(null)}
              onSaved={async () => { setEditing(null); await loadAll(); }}
            />
          ))}
          {editing === "new" && (
            <RateRow
              row={null}
              tenants={tenants}
              isEditing={true}
              onEdit={() => {}}
              onCancel={() => setEditing(null)}
              onSaved={async () => { setEditing(null); await loadAll(); }}
            />
          )}
        </div>
        <div className="mt-1.5" style={mono(9, "#9198b0")}>
          Standard transport requests for a matching carrier + (origin city → destination city) pair are auto-awarded at the contracted rate. Sonderfahrt always uses bidding.
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rate row — read-only + inline edit
// ---------------------------------------------------------------------------

const RateRow = ({ row, tenants, isEditing, onEdit, onCancel, onSaved }) => {
  const addToast = useStore((s) => s.addToast);
  const [carrier, setCarrier] = useState(row?.carrier_tenant_id || "");
  const [origin, setOrigin] = useState(row?.origin_city || "");
  const [destination, setDestination] = useState(row?.destination_city || "");
  const [rate, setRate] = useState(row?.agreed_rate != null ? String(row.agreed_rate) : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!carrier || !origin.trim() || !destination.trim() || !rate) {
      addToast({ type: "error", title: "Missing fields", body: "Carrier, both cities, and rate are required." });
      return;
    }
    const body = {
      carrier_tenant_id: carrier,
      origin_city: origin.trim(),
      destination_city: destination.trim(),
      agreed_rate: parseFloat(rate),
    };
    setBusy(true);
    try {
      if (row?.id) await axios.patch(`${API}/tms/contracted-rates/${row.id}`, body);
      else await axios.post(`${API}/tms/contracted-rates`, body);
      addToast({ type: "success", title: row?.id ? "Lane rate updated" : "Lane rate added", body: `${body.origin_city} → ${body.destination_city}` });
      onSaved();
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!row?.id) return;
    if (!window.confirm("Delete this contracted lane rate?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/tms/contracted-rates/${row.id}`);
      addToast({ type: "success", title: "Lane rate deleted" });
      onSaved();
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (isEditing) {
    return (
      <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
        style={{ borderTop: "1px solid rgba(255,255,255,0.04)", background: "rgba(24,95,165,0.06)" }}
        data-testid={row?.id ? `rate-edit-${row.id}` : "rate-edit-new"}>
        <select
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          className="col-span-3 px-2 py-1.5 rounded outline-none"
          style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", ...mono(11) }}
          data-testid="rate-edit-carrier"
        >
          <option value="">— select carrier —</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="e.g. Regensburg"
          value={origin}
          onChange={(e) => setOrigin(e.target.value)}
          className="col-span-3 px-2 py-1.5 rounded outline-none"
          style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", ...mono(11) }}
          data-testid="rate-edit-origin"
        />
        <input
          type="text"
          placeholder="e.g. Königsbrunn"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          className="col-span-3 px-2 py-1.5 rounded outline-none"
          style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", ...mono(11) }}
          data-testid="rate-edit-destination"
        />
        <input
          type="number"
          placeholder="0.00"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="col-span-2 px-2 py-1.5 rounded outline-none text-right"
          style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", ...mono(11) }}
          data-testid="rate-edit-rate"
        />
        <div className="col-span-1 flex justify-end gap-1">
          <button type="button" onClick={save} disabled={busy}
            className="px-2 py-1 rounded"
            style={{ background: "#185FA5", color: "#fff", ...mono(9, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer" }}
            data-testid="rate-edit-save">✓</button>
          <button type="button" onClick={onCancel}
            className="px-2 py-1 rounded"
            style={{ background: "transparent", color: "#9198b0", ...mono(9, undefined, 700, "0.4px"), border: "1px solid rgba(255,255,255,0.14)", cursor: "pointer" }}
            data-testid="rate-edit-cancel">×</button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center"
      style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
      data-testid={`rate-row-${row.id}`}>
      <div className="col-span-3 truncate" style={mono(11, "#f0f2f8", 600)}>{row.carrier_name || row.carrier_tenant_id}</div>
      <div className="col-span-3 truncate" style={mono(11, "#c5cad8")}>{row.origin_city}</div>
      <div className="col-span-3 truncate" style={mono(11, "#c5cad8")}>{row.destination_city}</div>
      <div className="col-span-2 text-right" style={mono(11, "#f3b14a", 700)}>{Number(row.agreed_rate).toLocaleString()} €</div>
      <div className="col-span-1 flex justify-end gap-1">
        <button type="button" onClick={onEdit}
          className="px-2 py-1 rounded"
          style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
          data-testid={`rate-row-edit-${row.id}`}>Edit</button>
        <button type="button" onClick={del} disabled={busy}
          className="px-2 py-1 rounded"
          style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
          data-testid={`rate-row-delete-${row.id}`}>Del</button>
      </div>
    </div>
  );
};
